import { io } from "socket.io-client";
import { host } from "@/apps/shared/Onoff/config";

let socket = null;
let activityTimer = null;
let socketToken = "";

function token() {
  return localStorage.token ? JSON.parse(localStorage.token) : "";
}

function currentTenantId() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}") || {};
    const tenant = Array.isArray(user.tenant) ? user.tenant[0] : user.tenant;
    return String(user.activeTenant?._id || user.activeTenant || tenant?._id || tenant || "").trim();
  } catch {
    return "";
  }
}

function currentUserId() {
  try {
    const rawToken = token();
    const base64 = rawToken.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "="))).id;
  } catch {
    return "";
  }
}

function entityId(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value._id || value.id || value.user_id || "";
}

function collectTenantIds(payload = {}) {
  const values = [
    payload.tenant_id,
    payload.tenantId,
    payload._tenant_id,
    payload.tenant,
    payload.external_movimentation?.tenant,
    payload.sale?.tenant,
    payload.transaction?.tenant,
    payload.conversation?.tenant,
    payload.message?.tenant,
  ];
  return values.map(entityId).map(String).filter(Boolean);
}

function shouldReceiveTenantPayload(payload = {}) {
  const activeTenantId = currentTenantId();
  if (!activeTenantId) return false;
  const tenantIds = [...new Set(collectTenantIds(payload))];
  return tenantIds.length > 0 && tenantIds.includes(activeTenantId);
}

function collectIds(target, keys) {
  return keys.flatMap(key => {
    const value = target?.[key];
    if (!value) return [];
    return Array.isArray(value) ? value.map(entityId) : [entityId(value)];
  }).map(String).filter(Boolean);
}

function internalChatParticipantIds(payload = {}) {
  const conversation = payload.conversation || {};
  const message = payload.message || {};
  return [
    ...collectIds(payload, ["participants", "participant_ids", "users"]),
    ...collectIds(conversation, ["participants", "participant_ids", "users"]),
    ...collectIds(message, ["sender", "recipient", "receiver", "to", "from"]),
    ...collectIds(message, ["sender_id", "recipient_id", "receiver_id", "to_user_id", "from_user_id"])
  ].map(String).filter(Boolean);
}

function shouldReceiveInternalChat(payload) {
  const participants = [...new Set(internalChatParticipantIds(payload))];
  if (!participants.length) return false;
  return participants.includes(String(currentUserId()));
}

export function startWhatsappBackendSocket() {
  const nextToken = token();
  if (socket) {
    if (socketToken && socketToken !== nextToken) {
      stopWhatsappBackendSocket();
      return startWhatsappBackendSocket();
    }
    socketToken = nextToken;
    socket.auth = { token: nextToken };
    if (!socket.connected) socket.connect();
    return;
  }
  socketToken = nextToken;
  socket = io(host, {
    auth: { token: nextToken },
    transports: ["websocket", "polling"],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 500,
    reconnectionDelayMax: 5000,
    timeout: 10000
  });
  const publishActivity = () => socket?.emit("user:activity");
  socket.on("connect", () => {
    publishActivity();
    window.dispatchEvent(new CustomEvent("stonepro:backend-socket-status", { detail: { connected: true } }));
  });
  socket.on("connect_error", () => {
    if (socket) socket.auth = { token: token() };
  });
  socket.on("disconnect", reason => {
    window.dispatchEvent(new CustomEvent("stonepro:backend-socket-status", { detail: { connected: false, reason } }));
  });
  activityTimer = window.setInterval(publishActivity, 60 * 1000);
  socket.on("whatsapp:conversation:incoming", payload => {
    if (!shouldReceiveTenantPayload(payload)) return;
    window.dispatchEvent(new CustomEvent("stonepro:whatsapp-offer", { detail: payload }));
  });
  socket.on("whatsapp:conversation:assigned", payload => {
    if (!shouldReceiveTenantPayload(payload)) return;
    window.dispatchEvent(new CustomEvent("stonepro:whatsapp-assigned", { detail: payload }));
  });
  socket.on("whatsapp:gateway-config:updated", payload => {
    if (!shouldReceiveTenantPayload(payload)) return;
    window.dispatchEvent(new CustomEvent("stonepro:whatsapp-gateway-config-updated", { detail: payload }));
  });
  socket.on("whatsapp:template:queued", payload => {
    if (!shouldReceiveTenantPayload(payload)) return;
    window.dispatchEvent(new CustomEvent("stonepro:whatsapp-template-queued", { detail: payload }));
  });
  socket.on("user:presence", payload => {
    if (!shouldReceiveTenantPayload(payload)) return;
    window.dispatchEvent(new CustomEvent("stonepro:user-presence", { detail: payload }));
  });
  socket.on("internal-chat:message", payload => {
    if (!shouldReceiveTenantPayload(payload)) return;
    if (!shouldReceiveInternalChat(payload)) return;
    window.dispatchEvent(new CustomEvent("stonepro:internal-chat-message", { detail: payload }));
  });
  socket.on("sale:updated", payload => {
    if (!shouldReceiveTenantPayload(payload)) return;
    window.dispatchEvent(new CustomEvent("stonepro:sale-updated", { detail: payload }));
  });
  socket.on("external-movimentation:updated", payload => {
    if (!shouldReceiveTenantPayload(payload)) return;
    window.dispatchEvent(new CustomEvent("stonepro:external-movimentation-updated", { detail: payload }));
  });
}

export function stopWhatsappBackendSocket() {
  window.clearInterval(activityTimer);
  activityTimer = null;
  socket?.disconnect();
  socket = null;
  socketToken = "";
}

export function acceptWhatsappConversation(conversationId) {
  return new Promise((resolve, reject) => {
    if (!socket?.connected) {
      reject(new Error("Canal em tempo real desconectado."));
      return;
    }
    socket.emit("whatsapp:conversation:accept", { conversation_id: conversationId }, response => {
      if (!response?.ok) {
        reject(new Error(response?.message || "Atendimento ja assumido."));
        return;
      }
      resolve(response.conversation);
    });
  });
}
